import {
  createStoryTextTypography,
  resolveTextDirection,
  StoryFontStyle,
  StoryTextTypography,
  TextBlockMeasurer
} from './textTypography';
import { BookDesignStyle, NormalizedRegion, PageLayoutTemplate } from '../types/layout';

export type PageDesignStyle = BookDesignStyle;
export type DeterministicLayoutTemplate = Exclude<PageLayoutTemplate, 'custom'>
  | 'text-bottom'
  | 'text-top';

export interface StoryTextBox {
  type: string;
  character?: string;
  content: string;
  fontFamily?: string;
  fontWeight?: string;
  fontStyle?: StoryFontStyle;
}

export interface LayoutRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PlannedTextElement extends LayoutRect {
  textBoxIndex: number;
  fontSize: number;
  fontFamily: string;
  fontWeight: string;
  fontStyle: StoryFontStyle;
  lineHeight: number;
  padding: number;
  color: string;
  backgroundColor: string;
  textAlign: 'left' | 'center' | 'right';
  verticalAlign: 'top';
  rotation: 0;
}

export interface DeterministicPageLayout {
  template: DeterministicLayoutTemplate;
  canonicalTemplate: Exclude<PageLayoutTemplate, 'custom'>;
  imageBounds: LayoutRect;
  panelBounds: LayoutRect;
  safeBounds: LayoutRect;
  textElements: PlannedTextElement[];
  panelBackgroundColor: string;
  panelBackgroundOpacity: number;
  fontSize: number;
  requiresSplit: boolean;
  allowsImageTextOverlap: boolean;
  imageRequired: boolean;
  fitStrategy: 'preferred' | 'expanded-panel' | 'reduced-font' | 'split-required';
  warnings: string[];
}

export interface BuildPageLayoutInput {
  canvasSize: { width: number; height: number };
  textBoxes: StoryTextBox[];
  template?: DeterministicLayoutTemplate;
  preferredFontSize?: number;
  minimumFontSize?: number;
  subjectRegions?: NormalizedRegion[];
  allowSubjectOverlap?: boolean;
  typography: StoryTextTypography;
  measureTextBlock: TextBlockMeasurer;
}

export interface ResolvedPageDesignStyle {
  requestedStyle: PageDesignStyle | undefined;
  resolvedStyle: PageDesignStyle;
  autoAdjusted: boolean;
  reason?: string;
}

export type ProviderAspectRatio = '1:1' | '3:4' | '4:3' | '9:16' | '16:9';

const PANEL_BACKGROUND = '#FFF8ED';
const TEXT_COLOR = '#2B2118';
const MIN_PANEL_FRACTION = 0.36;
const MAX_PANEL_FRACTION = 0.48;
const FULL_SCENE_NARRATION_WORD_LIMIT = 35;

export const PRINT_BLEED_INCHES = 0.125;
export const PAGE_PIXELS_PER_INCH = 100;
export const PRINT_BLEED_PIXELS = Math.round(PRINT_BLEED_INCHES * PAGE_PIXELS_PER_INCH);

// At the project's 100px-per-inch page scale this renders at roughly 13pt,
// keeping picture-book narration readable in print and on screen.
export const MIN_READABLE_NARRATION_FONT_SIZE = 18;

const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(maximum, Math.max(minimum, value));

export const resolvePageSafeBounds = (
  canvasSize: { width: number; height: number }
): LayoutRect => {
  const inset = clamp(Math.round(Math.min(canvasSize.width, canvasSize.height) * 0.05), 24, 44);
  return {
    x: inset,
    y: inset,
    width: Math.max(0, Math.round(canvasSize.width) - inset * 2),
    height: Math.max(0, Math.round(canvasSize.height) - inset * 2)
  };
};

export const resolveCanonicalTemplate = (
  style: PageDesignStyle
): PageLayoutTemplate | null => {
  switch (style) {
    case 'full-scene':
      return 'full-bleed-caption';
    case 'text-bottom':
      return 'image-top-text-bottom';
    case 'text-top':
      return 'text-top-image-bottom';
    case 'custom':
      return null;
    default:
      return style;
  }
};

const WIDE_GLYPH_PATTERN = /[\u2E80-\u9FFF\uAC00-\uD7AF\uF900-\uFAFF]/gu;

const countNarrationUnits = (content: string) => {
  const normalized = content.trim();
  const wordCount = normalized.split(/\s+/).filter(Boolean).length;
  const wideGlyphCount = normalized.match(WIDE_GLYPH_PATTERN)?.length || 0;
  return Math.max(wordCount, wideGlyphCount);
};

export const resolvePageDesignStyle = (
  requestedStyle: PageDesignStyle | undefined,
  textBoxes: StoryTextBox[]
): ResolvedPageDesignStyle => {
  const hasVisibleText = textBoxes.some(textBox => Boolean(String(textBox.content || '').trim()));
  if (!hasVisibleText && requestedStyle !== 'custom') {
    return {
      requestedStyle,
      resolvedStyle: 'full-scene',
      autoAdjusted: requestedStyle !== 'full-scene',
      reason: 'Pages without text do not reserve an empty narration panel.'
    };
  }

  if (!requestedStyle) {
    return {
      requestedStyle,
      resolvedStyle: 'text-bottom',
      autoAdjusted: true,
      reason: 'New and unconfigured books use the narration-safe layout.'
    };
  }

  if (requestedStyle !== 'full-scene') {
    return { requestedStyle, resolvedStyle: requestedStyle, autoAdjusted: false };
  }

  const narrationWordCount = textBoxes
    .filter((textBox) => textBox.type !== 'dialogue')
    .reduce((total, textBox) => total + countNarrationUnits(textBox.content || ''), 0);

  if (narrationWordCount > FULL_SCENE_NARRATION_WORD_LIMIT) {
    return {
      requestedStyle,
      resolvedStyle: 'text-bottom',
      autoAdjusted: true,
      reason: `Narration contains ${narrationWordCount} words; full-scene overlays are limited to ${FULL_SCENE_NARRATION_WORD_LIMIT}.`
    };
  }

  return { requestedStyle, resolvedStyle: requestedStyle, autoAdjusted: false };
};

const measureBlocks = (
  textBoxes: Array<{ textBox: StoryTextBox; sourceIndex: number }>,
  textWidth: number,
  fontSize: number,
  defaultTypography: StoryTextTypography,
  measureTextBlock: TextBlockMeasurer
) => {
  return textBoxes.map(({ textBox, sourceIndex }) => {
    const typography = createStoryTextTypography(
      textBox.fontFamily || defaultTypography.fontFamily,
      textBox.fontWeight || defaultTypography.fontWeight,
      textBox.fontStyle || defaultTypography.fontStyle,
      defaultTypography.padding
    );
    const measurement = measureTextBlock({
      content: textBox.content,
      width: textWidth,
      fontSize,
      typography: {
        ...typography,
        lineHeight: defaultTypography.lineHeight,
        fontAssetVersion: defaultTypography.fontAssetVersion
      }
    });

    if (!Number.isFinite(measurement.height) || measurement.height <= 0) {
      throw new Error(`Invalid text measurement for text box ${sourceIndex + 1}.`);
    }

    return {
      sourceIndex,
      content: textBox.content,
      height: Math.ceil(measurement.height),
      typography: measurement.typography
    };
  });
};

export const resolveProviderAspectRatio = (
  bounds: Pick<LayoutRect, 'width' | 'height'>
): ProviderAspectRatio => {
  if (bounds.width <= 0 || bounds.height <= 0) {
    throw new Error('Image bounds must have a positive width and height.');
  }

  const targetRatio = bounds.width / bounds.height;
  const candidates: Array<{ value: ProviderAspectRatio; ratio: number }> = [
    { value: '1:1', ratio: 1 },
    { value: '3:4', ratio: 3 / 4 },
    { value: '4:3', ratio: 4 / 3 },
    { value: '9:16', ratio: 9 / 16 },
    { value: '16:9', ratio: 16 / 9 }
  ];

  return candidates.reduce((closest, candidate) => (
    Math.abs(Math.log(targetRatio / candidate.ratio))
      < Math.abs(Math.log(targetRatio / closest.ratio))
      ? candidate
      : closest
  )).value;
};

export const isFullSceneIllustration = (
  imageBounds: Pick<LayoutRect, 'x' | 'y' | 'width' | 'height'>,
  canvasSize: Pick<LayoutRect, 'width' | 'height'>
) => imageBounds.x <= canvasSize.width * 0.1
  && imageBounds.y <= canvasSize.height * 0.1
  && imageBounds.x + imageBounds.width >= canvasSize.width * 0.9
  && imageBounds.y + imageBounds.height >= canvasSize.height * 0.9;

const rectIntersectionArea = (first: LayoutRect, second: LayoutRect) => {
  const width = Math.max(0, Math.min(first.x + first.width, second.x + second.width)
    - Math.max(first.x, second.x));
  const height = Math.max(0, Math.min(first.y + first.height, second.y + second.height)
    - Math.max(first.y, second.y));
  return width * height;
};

export const normalizedRegionToCanvasRect = (
  region: Pick<NormalizedRegion, 'x' | 'y' | 'width' | 'height'>,
  canvasSize: { width: number; height: number }
): LayoutRect => ({
  x: clamp(region.x, 0, 1) * canvasSize.width,
  y: clamp(region.y, 0, 1) * canvasSize.height,
  width: clamp(region.width, 0, 1 - clamp(region.x, 0, 1)) * canvasSize.width,
  height: clamp(region.height, 0, 1 - clamp(region.y, 0, 1)) * canvasSize.height
});

/** Converts image-relative semantic regions into canvas-normalized regions. */
export const mapImageRegionsToCanvas = (
  regions: NormalizedRegion[],
  imageBounds: LayoutRect,
  canvasSize: { width: number; height: number }
): NormalizedRegion[] => {
  if (canvasSize.width <= 0 || canvasSize.height <= 0
    || imageBounds.width <= 0 || imageBounds.height <= 0) return [];
  return regions.map(region => ({
    ...region,
    x: (imageBounds.x + region.x * imageBounds.width) / canvasSize.width,
    y: (imageBounds.y + region.y * imageBounds.height) / canvasSize.height,
    width: (region.width * imageBounds.width) / canvasSize.width,
    height: (region.height * imageBounds.height) / canvasSize.height
  }));
};

const subjectOverlapScore = (
  bounds: LayoutRect,
  regions: NormalizedRegion[],
  canvasSize: { width: number; height: number }
) => regions
  .filter(region => region.role !== 'text-safe')
  .reduce((total, region) => total + rectIntersectionArea(
    bounds,
    normalizedRegionToCanvasRect(region, canvasSize)
  ) * (region.confidence ?? 1), 0);

const buildTextOnlyLayout = ({
  canvasSize,
  textBoxes,
  preferredFontSize = 18,
  minimumFontSize = MIN_READABLE_NARRATION_FONT_SIZE,
  typography,
  measureTextBlock,
  safeInset
}: BuildPageLayoutInput & { safeInset?: number }): DeterministicPageLayout => {
  const width = Math.round(canvasSize.width);
  const height = Math.round(canvasSize.height);
  const visibleTextBoxes = (textBoxes || [])
    .map((textBox, sourceIndex) => ({ textBox, sourceIndex }))
    .filter(({ textBox }) => Boolean(String(textBox?.content || '').trim()));
  const safeBounds = safeInset === undefined
    ? resolvePageSafeBounds(canvasSize)
    : {
        x: safeInset,
        y: safeInset,
        width: Math.max(0, width - safeInset * 2),
        height: Math.max(0, height - safeInset * 2)
      };
  const horizontalInset = safeBounds.x;
  const verticalInset = safeBounds.y;
  const blockGap = clamp(Math.round(height * 0.016), 10, 18);
  const textWidth = safeBounds.width;
  const maximumTextHeight = safeBounds.height;
  const safeMinimumFontSize = clamp(
    Math.max(Math.round(minimumFontSize), MIN_READABLE_NARRATION_FONT_SIZE),
    MIN_READABLE_NARRATION_FONT_SIZE,
    32
  );
  const safePreferredFontSize = clamp(
    Math.max(Math.round(preferredFontSize), safeMinimumFontSize),
    safeMinimumFontSize,
    32
  );
  let fontSize = safePreferredFontSize;
  let measuredBlocks = measureBlocks(
    visibleTextBoxes,
    textWidth,
    fontSize,
    typography,
    measureTextBlock
  );
  const requiredHeight = () => measuredBlocks.reduce((sum, block) => sum + block.height, 0)
    + Math.max(0, measuredBlocks.length - 1) * blockGap;

  while (fontSize > safeMinimumFontSize && requiredHeight() > maximumTextHeight) {
    fontSize -= 1;
    measuredBlocks = measureBlocks(
      visibleTextBoxes,
      textWidth,
      fontSize,
      typography,
      measureTextBlock
    );
  }

  const contentHeight = requiredHeight();
  const requiresSplit = contentHeight > maximumTextHeight;
  let nextY = verticalInset + Math.max(0, Math.round((maximumTextHeight - contentHeight) / 2));
  const textElements = measuredBlocks.map(block => {
    const element: PlannedTextElement = {
      textBoxIndex: block.sourceIndex,
      x: horizontalInset,
      y: nextY,
      width: textWidth,
      height: block.height,
      fontSize,
      fontFamily: block.typography.fontFamily,
      fontWeight: block.typography.fontWeight,
      fontStyle: block.typography.fontStyle,
      lineHeight: block.typography.lineHeight,
      padding: block.typography.padding,
      color: TEXT_COLOR,
      backgroundColor: 'transparent',
      textAlign: resolveTextDirection(block.content) === 'rtl' ? 'right' : 'left',
      verticalAlign: 'top',
      rotation: 0
    };
    nextY += block.height + blockGap;
    return element;
  });

  return {
    template: 'text-only',
    canonicalTemplate: 'text-only',
    imageBounds: { x: 0, y: 0, width: 0, height: 0 },
    panelBounds: { x: 0, y: 0, width, height },
    safeBounds,
    textElements,
    panelBackgroundColor: PANEL_BACKGROUND,
    panelBackgroundOpacity: 1,
    fontSize,
    requiresSplit,
    allowsImageTextOverlap: false,
    imageRequired: false,
    fitStrategy: requiresSplit
      ? 'split-required'
      : fontSize < safePreferredFontSize ? 'reduced-font' : 'expanded-panel',
    warnings: requiresSplit
      ? ['This text-only page is full. Continue the remaining copy on another text-only page.']
      : []
  };
};

export const buildDeterministicPageLayout = ({
  canvasSize,
  textBoxes,
  template = 'text-bottom',
  preferredFontSize = 18,
  minimumFontSize = MIN_READABLE_NARRATION_FONT_SIZE,
  subjectRegions = [],
  allowSubjectOverlap = false,
  typography,
  measureTextBlock
}: BuildPageLayoutInput): DeterministicPageLayout => {
  const width = Math.round(canvasSize.width);
  const height = Math.round(canvasSize.height);

  if (!Number.isFinite(width) || !Number.isFinite(height) || width < 100 || height < 100) {
    throw new Error('Canvas dimensions must be finite values of at least 100 pixels.');
  }

  if (template === 'text-only') {
    return buildTextOnlyLayout({
      canvasSize,
      textBoxes,
      template,
      preferredFontSize,
      minimumFontSize,
      typography,
      measureTextBlock
    });
  }

  if (template === 'split') {
    const imageWidth = Math.round(width * (width > height ? 0.58 : 0.52));
    const pageSafeBounds = resolvePageSafeBounds(canvasSize);
    const textLayout = buildTextOnlyLayout({
      canvasSize: { width: width - imageWidth, height },
      textBoxes,
      preferredFontSize,
      minimumFontSize,
      typography,
      measureTextBlock,
      safeInset: pageSafeBounds.x
    });
    return {
      ...textLayout,
      template: 'split',
      canonicalTemplate: 'split',
      imageBounds: { x: 0, y: 0, width: imageWidth, height },
      panelBounds: { x: imageWidth, y: 0, width: width - imageWidth, height },
      safeBounds: { ...textLayout.safeBounds, x: textLayout.safeBounds.x + imageWidth },
      textElements: textLayout.textElements.map(element => ({
        ...element,
        x: element.x + imageWidth
      })),
      imageRequired: true
    };
  }

  if (template === 'image-top-text-bottom'
    || template === 'text-top-image-bottom'
    || template === 'dialogue'
    || template === 'title'
    || template === 'full-bleed-caption') {
    let protectedTemplate: 'text-bottom' | 'text-top' = template === 'text-top-image-bottom'
      ? 'text-top'
      : 'text-bottom';

    if (template === 'full-bleed-caption' && subjectRegions.length > 0) {
      const candidateHeight = Math.round(height * MAX_PANEL_FRACTION);
      const bottomCandidate = { x: 0, y: height - candidateHeight, width, height: candidateHeight };
      const topCandidate = { x: 0, y: 0, width, height: candidateHeight };
      protectedTemplate = subjectOverlapScore(topCandidate, subjectRegions, canvasSize)
        < subjectOverlapScore(bottomCandidate, subjectRegions, canvasSize)
        ? 'text-top'
        : 'text-bottom';
    }

    const protectedLayout = buildDeterministicPageLayout({
      canvasSize,
      textBoxes,
      template: protectedTemplate,
      preferredFontSize: template === 'title' ? Math.max(preferredFontSize, 28) : preferredFontSize,
      minimumFontSize,
      typography,
      measureTextBlock
    });
    const canonicalTemplate: Exclude<PageLayoutTemplate, 'custom'> = template;
    const fullBleed = template === 'full-bleed-caption';
    const overlapScore = fullBleed
      ? subjectOverlapScore(protectedLayout.panelBounds, subjectRegions, canvasSize)
      : 0;
    const warnings = [...protectedLayout.warnings];
    if (fullBleed && overlapScore > 0 && !allowSubjectOverlap) {
      warnings.push('The caption intersects an important subject. Use a protected or split template before export.');
    }

    return {
      ...protectedLayout,
      template,
      canonicalTemplate,
      imageBounds: fullBleed
        ? { x: 0, y: 0, width, height }
        : protectedLayout.imageBounds,
      textElements: protectedLayout.textElements.map(element => ({
        ...element,
        textAlign: template === 'title'
          ? 'center'
          : element.textAlign,
        backgroundColor: fullBleed || template === 'dialogue'
          ? PANEL_BACKGROUND
          : 'transparent'
      })),
      panelBackgroundOpacity: fullBleed ? 0.92 : 1,
      allowsImageTextOverlap: fullBleed,
      warnings
    };
  }

  const visibleTextBoxes = (textBoxes || [])
    .map((textBox, sourceIndex) => ({ textBox, sourceIndex }))
    .filter(({ textBox }) => Boolean(String(textBox?.content || '').trim()));

  const safeBounds = resolvePageSafeBounds(canvasSize);
  const horizontalInset = Math.max(
    clamp(Math.round(width * 0.06), 24, 44),
    safeBounds.x
  );
  const verticalInset = Math.max(
    clamp(Math.round(height * 0.026), 18, 30),
    safeBounds.y
  );
  const blockGap = clamp(Math.round(height * 0.012), 8, 14);
  const textWidth = width - (horizontalInset * 2);
  const minPanelHeight = Math.round(height * MIN_PANEL_FRACTION);
  const maxPanelHeight = Math.round(height * MAX_PANEL_FRACTION);
  const safeMinimumFontSize = clamp(
    Math.max(Math.round(minimumFontSize), MIN_READABLE_NARRATION_FONT_SIZE),
    MIN_READABLE_NARRATION_FONT_SIZE,
    32
  );
  const safePreferredFontSize = clamp(
    Math.max(Math.round(preferredFontSize), safeMinimumFontSize),
    safeMinimumFontSize,
    32
  );

  let fontSize = safePreferredFontSize;
  let measuredBlocks = measureBlocks(
    visibleTextBoxes,
    textWidth,
    fontSize,
    typography,
    measureTextBlock
  );
  const requiredHeight = () => measuredBlocks.reduce((sum, block) => sum + block.height, 0)
    + Math.max(0, measuredBlocks.length - 1) * blockGap
    + verticalInset * 2;

  while (fontSize > safeMinimumFontSize && requiredHeight() > maxPanelHeight) {
    fontSize -= 1;
    measuredBlocks = measureBlocks(
      visibleTextBoxes,
      textWidth,
      fontSize,
      typography,
      measureTextBlock
    );
  }

  const contentRequiredHeight = requiredHeight();
  const requiresSplit = contentRequiredHeight > maxPanelHeight;
  const panelHeight = clamp(contentRequiredHeight, minPanelHeight, maxPanelHeight);
  const panelY = template === 'text-top' ? 0 : height - panelHeight;
  const imageY = template === 'text-top' ? panelHeight : 0;
  const imageHeight = height - panelHeight;
  const panelBounds = { x: 0, y: panelY, width, height: panelHeight };
  const imageBounds = { x: 0, y: imageY, width, height: imageHeight };
  const totalBlockHeight = measuredBlocks.reduce((sum, block) => sum + block.height, 0)
    + Math.max(0, measuredBlocks.length - 1) * blockGap;
  let nextY = panelY + Math.max(verticalInset, Math.round((panelHeight - totalBlockHeight) / 2));

  const textElements = measuredBlocks.map((block) => {
    const element: PlannedTextElement = {
      textBoxIndex: block.sourceIndex,
      x: horizontalInset,
      y: nextY,
      width: textWidth,
      height: block.height,
      fontSize,
      fontFamily: block.typography.fontFamily,
      fontWeight: block.typography.fontWeight,
      fontStyle: block.typography.fontStyle,
      lineHeight: block.typography.lineHeight,
      padding: block.typography.padding,
      color: TEXT_COLOR,
      backgroundColor: 'transparent',
      textAlign: resolveTextDirection(block.content) === 'rtl' ? 'right' : 'left',
      verticalAlign: 'top',
      rotation: 0
    };
    nextY += block.height + blockGap;
    return element;
  });

  const warnings: string[] = [];
  if (requiresSplit) {
    warnings.push('This page contains more text than the protected panel can fit. Split the copy across two pages.');
  }

  return {
    template,
    canonicalTemplate: template === 'text-top'
      ? 'text-top-image-bottom'
      : 'image-top-text-bottom',
    imageBounds,
    panelBounds,
    safeBounds,
    textElements,
    panelBackgroundColor: PANEL_BACKGROUND,
    panelBackgroundOpacity: 1,
    fontSize,
    requiresSplit,
    allowsImageTextOverlap: false,
    imageRequired: true,
    fitStrategy: requiresSplit
      ? 'split-required'
      : fontSize < safePreferredFontSize
        ? 'reduced-font'
        : panelHeight > minPanelHeight ? 'expanded-panel' : 'preferred',
    warnings
  };
};

export const resolvePlannedTextHeight = (
  plannedHeight: number,
  calculatedHeight: number,
  minimumHeight: number,
  deterministic: boolean
) => deterministic
  ? Math.max(plannedHeight, minimumHeight)
  : Math.max(plannedHeight, calculatedHeight, minimumHeight);

export const resolveTextLayout = async <T>(
  deterministicLayout: DeterministicPageLayout | null,
  analyzeScene: () => Promise<T>
): Promise<T | { textElements: PlannedTextElement[]; designNotes: string }> => {
  if (deterministicLayout) {
    return {
      textElements: deterministicLayout.textElements,
      designNotes: `Deterministic ${deterministicLayout.template} layout`
    };
  }

  return analyzeScene();
};
